import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Checkbox, Fieldset, Select, Stack, Text, TextInput } from "@mantine/core";
import { MIME_TYPES } from "@mantine/dropzone";
import { isNotEmpty, useForm } from "@mantine/form";
import { calculateFileSize } from "../../../helpers/fileHelper";
import { notEmptyMessage } from "../../../helpers/formHelper";
import { getFullName } from "../../../helpers/stringHelper";
import type { Apprentice } from "../../../models/apprentice";
import type { Document } from "../../../models/document";
import { type DocumentType, getDocumentTypeOptions } from "../../../models/enums/documentType";
import { AccessConfigForm, type AccessConfigFormType } from "../../accessConfig/AccessConfigForm";
import { SaveButton } from "../../button/SaveButton";
import { DropZone } from "../../dropzone/Dropzone";
import { emailTemplateValidation } from "../../email/EmailEditorForm";
import { FilterSelect } from "../../select/FilterSelect";
import { BaseDocumentForm, type BaseDocumentFormType } from "../BaseDocumentForm";

type DocumentFormProps = {
  document?: Document;
  apprentices: Apprentice[];
  onSubmit: (value: RequestPayload) => void;
  saveButtonLabel: string;
};

type FormType = AccessConfigFormType &
  BaseDocumentFormType & {
    file?: File;
    title?: string;
    apprentice_id?: string;
    publish?: boolean;
    document_type?: DocumentType;
  };

type FormErrors = {
  file?: string[];
};

export function DocumentForm({ document, apprentices, onSubmit, saveButtonLabel }: DocumentFormProps) {
  const form = useForm<FormType>({
    mode: "uncontrolled",
    initialValues: {
      file: document?.file,
      title: document?.title,
      document_type: document?.document_type,
      apprentice_id: document?.apprentice?.id.toString(),
      publish: document?.publish ?? false,
      document_readable: document?.document_readable ?? [],
      document_signable: document?.document_signable ?? [],
      email_template: document?.email_template,
      subject: document?.subject,
      send_mail: document?.send_mail ?? true,
    },
    validate: {
      apprentice_id: isNotEmpty(notEmptyMessage),
      document_type: isNotEmpty(notEmptyMessage),
      title: isNotEmpty(notEmptyMessage),
      subject: (value, values) => (values.send_mail ? isNotEmpty(notEmptyMessage)(value) : null),
      email_template: (value, values) => (values.send_mail ? emailTemplateValidation(value) : null),
    },
  });
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const selectedFile = (
    <>
      {form.getValues().file && (
        <Text>
          <b>{form.getValues().file?.name}</b> {calculateFileSize(form.getValues().file?.size)}
        </Text>
      )}
    </>
  );

  return (
    <BaseDocumentForm saveButtonLabel={saveButtonLabel} form={form} onSubmit={onSubmit} disabled={document?.publish}>
      <Fieldset legend="Details">
        <Stack maw="60rem">
          {!document ? (
            <FilterSelect
              label="Lernende/r"
              data={apprentices.map((a) => ({ label: getFullName(a), value: a.id.toString() }))}
              withAsterisk
              key={form.key("apprentice_id")}
              {...form.getInputProps("apprentice_id")}
            />
          ) : (
            <TextInput label="Lernende/r" disabled withAsterisk value={getFullName(document.apprentice)} />
          )}
          <TextInput
            disabled={document?.publish}
            label="Titel"
            withAsterisk
            key={form.key("title")}
            {...form.getInputProps("title")}
            error={errors?.title}
          />
          <Select
            label="Dokument Typ"
            data={getDocumentTypeOptions()}
            withAsterisk
            key={form.key("document_type")}
            {...form.getInputProps("document_type")}
            maxDropdownHeight="15rem"
            disabled={document?.publish}
          />
          <DropZone
            mimeTypes={[MIME_TYPES.pdf]}
            onDrop={(files) => form.setFieldValue("file", files[0])}
            multiple={false}
            error={errors?.file}
            disabled={document?.publish}
          />
          {selectedFile}
          <Checkbox label="Freigeben" key={form.key("publish")} {...form.getInputProps("publish", { type: "checkbox" })} />
          <AccessConfigForm form={form} disabled={document?.publish} />
          <SaveButton label={saveButtonLabel} />
        </Stack>
      </Fieldset>
    </BaseDocumentForm>
  );
}
